import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Header from '@/components/Header';
import { ForkKnife, Fire, Barbell } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MealsPage = ({ user }) => {
  const [meals, setMeals] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchMeals = useCallback(async () => {
    try {
      const url = selectedCategory === 'all'
        ? `${BACKEND_URL}/api/meals`
        : `${BACKEND_URL}/api/meals?category=${selectedCategory}`;
      const response = await axios.get(url);
      setMeals(response.data);
    } catch (error) {
      toast.error('Could not load meals. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchMeals();
  }, [fetchMeals]);

  const categories = [
    { value: 'all', label: 'All Meals' },
    { value: 'fruit-salad', label: 'Fruit Salad' },
    { value: 'pre-workout', label: 'Pre-Workout' },
    { value: 'post-workout', label: 'Post-Workout' },
    { value: 'lunch', label: 'Lunch' },
    { value: 'evening', label: 'Evening' },
    { value: 'dinner', label: 'Dinner' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F0]">
        <Header user={user} />
        <div className="flex items-center justify-center py-24">
          <div className="w-16 h-16 border-4 border-[#1A4331] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Header user={user} />

      <main className="px-6 md:px-12 lg:px-24 py-12" data-testid="meals-page">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <ForkKnife size={40} weight="duotone" className="text-[#FBBF24]" />
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#1C1917] font-heading" data-testid="meals-heading">Our Menu</h1>
          </div>
          <p className="text-base leading-relaxed text-stone-600 max-w-2xl">Explore our range of healthy, nutritious meals with complete macro information.</p>
        </motion.div>

        {/* Category Filter */}
        <div className="mb-8 flex flex-wrap gap-3">
          {categories.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setSelectedCategory(cat.value)}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-200 ${
                selectedCategory === cat.value
                  ? 'bg-[#1A4331] text-white'
                  : 'bg-white border border-stone-200 text-stone-700 hover:border-[#1A4331]'
              }`}
              data-testid={`category-${cat.value}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Meals Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {meals.map((meal, index) => (
            <motion.div
              key={meal.meal_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * index }}
              className="bg-white border border-stone-200 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-all duration-300 rounded-2xl overflow-hidden"
              data-testid={`meal-card-${meal.meal_id}`}
            >
              <img src={meal.image_url} alt={meal.name} className="w-full h-48 object-cover" />
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-medium text-[#1C1917] font-heading">{meal.name}</h3>
                  {meal.is_veg && <span className="text-xs bg-[#34D399] text-white px-2 py-1 rounded-full">VEG</span>}
                </div>
                <p className="text-sm text-stone-600 mb-4">{meal.description}</p>
                
                {/* Nutrition Info */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-stone-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Fire size={16} weight="duotone" className="text-[#C05C3D]" />
                      <span className="text-xs text-stone-500 uppercase tracking-wider">Calories</span>
                    </div>
                    <p className="text-lg font-bold text-[#1C1917]">{meal.calories}</p>
                  </div>
                  <div className="bg-stone-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Barbell size={16} weight="duotone" className="text-[#34D399]" />
                      <span className="text-xs text-stone-500 uppercase tracking-wider">Protein</span>
                    </div>
                    <p className="text-lg font-bold text-[#1C1917]">{meal.protein}g</p>
                  </div>
                </div>

                {/* Macro Bars */}
                <div className="space-y-2 mb-4">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-stone-600">Carbs</span>
                      <span className="font-medium">{meal.carbs}g</span>
                    </div>
                    <div className="w-full bg-stone-200 rounded-full h-2">
                      <div className="bg-[#C05C3D] h-2 rounded-full" style={{ width: `${(meal.carbs / 100) * 100}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-stone-600">Fats</span>
                      <span className="font-medium">{meal.fats}g</span>
                    </div>
                    <div className="w-full bg-stone-200 rounded-full h-2">
                      <div className="bg-[#FBBF24] h-2 rounded-full" style={{ width: `${(meal.fats / 50) * 100}%` }}></div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-stone-200">
                  <span className="text-xs text-stone-500 uppercase tracking-wider">{meal.category.replace('-', ' ')}</span>
                  <span className="text-lg font-bold text-[#1A4331]">₹{meal.price}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {meals.length === 0 && (
          <div className="text-center py-12">
            <p className="text-stone-600">No meals found in this category.</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default MealsPage;